import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties } from 'react';
import type { BoardSettings, Card } from '../types/board';

interface SortableCardProps {
  card: Card;
  settings: BoardSettings;
  isAdmin?: boolean;
  userId?: string;
  accentColor?: string;
  revealAllCards?: boolean;
  onEdit?: () => void;
  onDelete?: () => Promise<void>;
  onVote?: () => Promise<void>;
  onRemoveVote?: () => Promise<void>;
  viewerVoteTotal?: number;
}

const DEFAULT_CARD_ACCENT = '#4F7EE8';

function normalizeAccentColor(color: string | undefined): string {
  if (color && /^#[0-9A-Fa-f]{6}$/.test(color)) {
    return color;
  }
  return DEFAULT_CARD_ACCENT;
}

function hexToRgba(hexColor: string, alpha: number): string {
  const red = parseInt(hexColor.slice(1, 3), 16);
  const green = parseInt(hexColor.slice(3, 5), 16);
  const blue = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function EditIcon(): JSX.Element {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function DeleteIcon(): JSX.Element {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function formatVoteCount(count: number): string {
  return count === 1 ? '1 vote' : `${count} votes`;
}

export function SortableCard({
  card,
  settings,
  isAdmin = false,
  userId,
  accentColor,
  revealAllCards = false,
  onEdit,
  onDelete,
  onVote,
  onRemoveVote,
  viewerVoteTotal = 0,
}: SortableCardProps): JSX.Element {
  const isAuthor = card.isAuthor === true || (!!userId && card.createdBy === userId);
  const canDrag = isAdmin || isAuthor;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
    data: { card, type: 'card' },
    disabled: { draggable: !canDrag, droppable: false },
  });
  const normalizedAccent = normalizeAccentColor(accentColor);
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    borderColor: normalizedAccent,
    backgroundColor: hexToRgba(normalizedAccent, 0.08),
  };
  const canEdit = !!onEdit && (isAdmin || isAuthor);
  const canDelete = !!onDelete && (isAdmin || isAuthor);
  const canSeeContent = settings.cardVisibility === 'VISIBLE' || isAuthor || revealAllCards;
  const showAuthor = isAdmin ? settings.showAuthorForAdmin : settings.showAuthorForParticipants;
  const displayContent = canSeeContent && card.content !== '[hidden]' ? card.content : '[Content hidden]';
  const votingEnabled = settings.votingEnabled;
  const viewerVoteCount = card.viewerVoteCount ?? 0;
  const voteLimitReached = viewerVoteTotal >= settings.votesPerParticipant;
  const cardVoteLimitReached = !settings.allowMultipleVotesPerCard && viewerVoteCount > 0;
  const canAddVote = !!onVote && votingEnabled && !voteLimitReached && !cardVoteLimitReached;
  const canRemoveVote = !!onRemoveVote && votingEnabled && viewerVoteCount > 0;
  const totalVotesVisible = !card.votesHidden && typeof card.votes === 'number';

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid="board-card"
      data-card-id={card.id}
      data-card-column-id={card.columnId}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      className={`${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'} rounded-lg border p-3 shadow-sm transition ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}`}>
      <div className="flex items-start gap-3">
        <p className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-sm font-semibold text-gray-900 dark:text-gray-100 ${!canSeeContent && settings.cardVisibility === 'BLURRED' ? 'blur-sm select-none' : ''}`}>
          {displayContent}
        </p>
        <div className="flex flex-shrink-0 items-center gap-1">
          {canEdit ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onEdit?.();
              }}
              onPointerDown={(event) => event.stopPropagation()}
              className="flex h-7 w-7 items-center justify-center rounded text-gray-500 transition hover:bg-black/10 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label="Edit card"
              title="Edit card"
            >
              <EditIcon />
            </button>
          ) : (
            <span
              className="flex h-7 w-7 items-center justify-center text-gray-400 opacity-60 dark:text-gray-500"
              title="Only the card author or board admin can edit this card"
            >
              <EditIcon />
            </span>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                void onDelete().catch((error) => {
                  console.error('Failed to delete card:', error);
                });
              }}
              onPointerDown={(event) => event.stopPropagation()}
              className="flex h-7 w-7 items-center justify-center rounded text-red-600 transition hover:bg-red-500/10 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-red-400 dark:hover:bg-red-500/20 dark:hover:text-red-300"
              aria-label="Delete card"
              title="Delete card"
            >
              <DeleteIcon />
            </button>
          )}
        </div>
      </div>
      {votingEnabled && (
        <div
          className="mt-3 flex items-center justify-between gap-3 rounded-md border border-black/10 bg-white/70 px-2 py-2 text-xs font-semibold text-gray-700 dark:border-white/10 dark:bg-gray-950/30 dark:text-gray-200"
          data-testid="card-voting"
        >
          <div className="min-w-0">
            <div data-testid="card-total-votes">
              {totalVotesVisible ? formatVoteCount(card.votes ?? 0) : 'Votes hidden'}
            </div>
            <div className="text-gray-500 dark:text-gray-400" data-testid="card-your-votes">
              You: {viewerVoteCount}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                void onRemoveVote?.().catch((error) => {
                  console.error('Failed to remove vote:', error);
                });
              }}
              onPointerDown={(event) => event.stopPropagation()}
              disabled={!canRemoveVote}
              className="flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-base leading-none text-gray-700 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              aria-label="Remove vote"
              title="Remove vote"
            >
              -
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                void onVote?.().catch((error) => {
                  console.error('Failed to add vote:', error);
                });
              }}
              onPointerDown={(event) => event.stopPropagation()}
              disabled={!canAddVote}
              className="flex h-7 w-7 items-center justify-center rounded bg-blue-600 text-base leading-none text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Add vote"
              title={voteLimitReached ? 'Vote limit reached' : 'Add vote'}
            >
              +
            </button>
          </div>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-xs font-semibold text-gray-600 dark:text-gray-300">
        <span>{showAuthor ? card.author : 'Anonymous'}</span>
        {settings.showCardDate && (
          <span>{new Date(card.createdAt).toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
}
